import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Chip, Divider, TextField } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 1100,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    textAlign: 'left',

    [theme.breakpoints.down('sm')]: {
      padding: '10px 8px 0px',
    },
  },
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background:
      'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 14,

    [theme.breakpoints.down('sm')]: {
      padding: '10px 10px 10px',
      marginBottom: 10,
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '2.0rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  subtitle: {
    marginTop: 8,
    color: '#cfd8ff',
    lineHeight: 1.6,
    fontSize: 13,
    maxWidth: 920,
    textAlign: 'left',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      padding: '6px 10px',
    },
  },
  ghostButton: {
    textTransform: 'none',
    fontWeight: 800,
    color: '#cfd3ff',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 10,
    padding: '8px 12px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      padding: '6px 10px',
    },
  },
  controlsRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  section: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    padding: 16,
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    color: '#e5e7ff',
    marginBottom: 10,
    textAlign: 'left',
  },
  card: {
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(5, 8, 20, 0.68)',
    padding: 14,
    marginBottom: 10,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: 0.2,
  },
  cardTypeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 6,
  },
  chip: {
    height: 20,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    backgroundColor: 'rgba(77, 121, 255, 0.15)',
    border: '1px solid rgba(77, 121, 255, 0.45)',
    color: '#e5e7ff',
    fontWeight: 800,
  },
  chipRabbit: {
    backgroundColor: 'rgba(158, 247, 197, 0.12)',
    border: '1px solid rgba(158, 247, 197, 0.35)',
    color: '#9ef7c5',
  },
  body: {
    marginTop: 10,
    fontSize: 13,
    color: '#e8f5ff',
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
  },
  label: {
    fontWeight: 900,
    color: '#9ef7c5',
  },
  divider: {
    backgroundColor: 'rgba(255,255,255,0.10)',
    margin: '12px 0',
  },
  searchField: {
    flex: '1 1 260px',
    '& .MuiInputBase-root': {
      color: '#e5e7ff',
      background: 'rgba(5, 8, 20, 0.62)',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.10)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputLabel-root': {
      color: '#9ea4d8',
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiInputBase-input': {
        fontSize: 11,
        padding: '6px 8px',
      },
    },
  },
}));

const STORY_TYPES = [
  {
    id: 'THESIS',
    family: 'rabbit',
    title: 'Rabbit Thesis',
    tags: ['meta', 'narrative'],
    what: 'Bundles multiple rabbit findings from the same window into a coherent intelligence brief.',
    why: 'Turns raw patterns into “what happened / why it matters / what to watch next.” This is the main investor-facing narrative layer.',
    evidence: 'Evidence is derived from linked rabbit findings (funnel/cluster/loop/mirror/staging). Graphs vary by underlying findings.',
  },
  {
    id: 'STAGING',
    family: 'rabbit',
    title: 'Rabbit Staging',
    tags: ['predictive', 'readiness', 'time'],
    what: 'Detects wallets/subgraphs that “wake up” (dormancy break) and acquire readiness (funding, tool-touch, infra proximity) before an on-chain action.',
    why: 'Forward-looking signal: preparation often precedes funnels/loops/mirrors. Useful for watchlists and early risk review.',
    evidence: 'Graph is a bounded staging subgraph anchored on a seed wallet. Includes edges with temporal sequencing; may include 2-hop peer expansion for trails.',
  },
  {
    id: 'FUNNEL',
    family: 'rabbit',
    title: 'Rabbit Funnel',
    tags: ['convergence', 'routing'],
    what: 'Builds 2-hop paths (seed → mid → sink) and detects convergence where many sources route into few sinks.',
    why: 'High-signal consolidation patterns: staging, infra aggregation, collection points, or operational sinks.',
    evidence: 'Graph emphasizes sources/mids/sinks and deduped sink components. Designed to be sparse and explainable.',
  },
  {
    id: 'CLUSTER',
    family: 'rabbit',
    title: 'Rabbit Cluster',
    tags: ['structure', 'topology'],
    what: 'Expands a local graph around funnel sinks and measures topology (density, reciprocity, hubs) to infer structural intent.',
    why: 'Explains operational design: infra meshes, coordination structures, staging complexes, etc.',
    evidence: 'Graph contains multi-depth neighborhood expansion and topology metrics used by UI to reconstruct trails.',
  },
  {
    id: 'LOOP',
    family: 'rabbit',
    title: 'Rabbit Loop',
    tags: ['recycling', 'wash', 'obfuscation'],
    what: 'Detects circular / recycling paths inside bounded subgraphs (closed or near-closed loops).',
    why: 'Strong signal for obfuscation, wash-like behavior, liquidity recycling, testing loops, or laundering tactics.',
    evidence: 'Graph includes cycle paths and supporting edges within the bounded subgraph used to detect the loop.',
  },
  {
    id: 'MIRROR',
    family: 'rabbit',
    title: 'Rabbit Mirror',
    tags: ['coordination', 'bot-swarm'],
    what: 'Detects groups of wallets with similar counterparties/timing/behavior (coordinated or copy-trading patterns).',
    why: 'Reveals coordination that doesn’t require explicit direct edges between participants.',
    evidence: 'Graph is a complete representation of the group and shared counterparties (often group→counterparty bipartite connections).',
  },
  {
    id: 'FLOW',
    family: 'standard',
    title: 'Flow',
    tags: ['wallet', 'graph'],
    what: 'Chooses an anomalous wallet (high tx + peers) and stores a recent evidence graph of its direct interactions.',
    why: 'Fast “what is this wallet doing?” view; good for investigation entry points.',
    evidence: 'Explicit node/edge graph stored in story meta for UI reconstruction.',
  },
  {
    id: 'WHALE',
    family: 'standard',
    title: 'Whale',
    tags: ['large-mover', 'net-flow'],
    what: 'Large “whale” activity stories emitted by whale scripts (behavior shift and stealth net flow).',
    why: 'Investor-grade positioning signal: accumulation/distribution and stealth routing behavior.',
    evidence: 'Evidence is metrics-heavy (counts, usd flow, counterparties). Graphing is typically via evidence exploration views rather than a dedicated stored subgraph.',
  },
  {
    id: 'HOTLINKS',
    family: 'standard',
    title: 'Hot Links',
    tags: ['token', 'activity'],
    what: 'Tokens with unusually high transaction activity in a short window, with dedupe/cooldown controls.',
    why: 'Surface “something is happening here” tokens without claiming intent.',
    evidence: 'Evidence uses the /links graph and counterparty expansion around the token.',
  },
  {
    id: 'SURGE',
    family: 'standard',
    title: 'Surge',
    tags: ['price', 'activity'],
    what: 'Price surge events paired with on-chain touch activity context.',
    why: 'Separates broad participation from thin liquidity spikes; helps interpret price moves.',
    evidence: 'Evidence is price change + activity deltas; investigation typically uses token evidence graph.',
  },
  {
    id: 'ACCUMULATION',
    family: 'standard',
    title: 'Accumulation',
    tags: ['whale', 'net-flow', 'buying'],
    what: 'Detects a single whale address net-buying a token at scale over the lookback window.',
    why: 'Surfaces large, directional capital movement early so you can watch or trade the token before the move is obvious in price.',
    evidence: 'Evidence includes the whale address, token address, net USD inflow, transfer counts, counterparties, and market-cap context.',
  },
  {
    id: 'BREAKOUT_CONFIRMED',
    family: 'standard',
    title: 'Breakout Confirmed',
    tags: ['momentum', 'confirmation', 'price'],
    what: 'Confirms a breakout when a MOVER story and an upgraded SURGE story both fire on the same token.',
    why: 'Filters out thin-liquidity noise and gives you a higher-conviction momentum signal than price alone.',
    evidence: 'Evidence links the source MOVER and SURGE stories together, including timing delta, scores, and participation quality.',
  },
  {
    id: 'CATEGORY',
    family: 'standard',
    title: 'Category',
    tags: ['rotation', 'sector'],
    what: 'Category momentum/rotation across a set of tokens (leaders + category cohort).',
    why: 'Investor-friendly “sector rotation” lens that’s hard to see from single-token charts.',
    evidence: 'Evidence is the category cohort list and leader moves; drilldown via token evidence graphs.',
  },
  {
    id: 'DISCOVERY',
    family: 'standard',
    title: 'Discovery',
    tags: ['new-assets', 'unknown'],
    what: 'New / unknown contracts trending (heuristic filter to avoid duplicating HOTLINKS).',
    why: 'Find newly surfaced tokens/contracts early, before they’re widely tagged or understood.',
    evidence: 'Evidence is activity + identity metadata; drilldown via /links graph.',
  },
  {
    id: 'ECOSYSTEM_SHIFT',
    family: 'standard',
    title: 'Ecosystem Shift',
    tags: ['macro', 'rotation'],
    what: 'Cross-chain day-over-day volume change (macro rotation signal).',
    why: 'Top-down situational awareness for capital rotation between ecosystems.',
    evidence: 'Evidence is aggregate stats per chain; not graph-centric.',
  },
  {
    id: 'SANDWICH',
    family: 'standard',
    title: 'Sandwich',
    tags: ['mev', 'execution', 'bot'],
    what: 'Detects MEV sandwich attacks on Uniswap V2/V3 swaps: a three-transaction sequence in the same block where a bot buys a token ahead of a victim swap (front-run, raising the price), the victim executes at a worse price, then the bot immediately sells (back-run, pocketing the spread). Detection matches triplets by same bot address for front/back-run, different victim address in the middle, and the same non-stable token appearing in all three transactions\' Transfer logs.',
    why: 'Direct execution-quality signal: surfaces which tokens and routes are actively being sandwiched, which bots are doing it, and the estimated profit per attack. A pattern of SANDWICH stories from the same bot address builds a threat profile useful for routing decisions.',
    evidence: 'Evidence is transaction-level: block number, front/victim/back tx hashes, bot address, victim address, token address, estimated profit in USD, ETH values, and tx indices. Investigation drilldown via /links graph on the bot address to map its full counterparty network.',
  },
  {
    id: 'LIQUIDITY_DRAIN',
    family: 'standard',
    title: 'Liquidity Drain',
    tags: ['risk', 'tvl', 'slippage'],
    what: 'Flags pools whose TVL is falling sharply before price fully reflects the damage.',
    why: 'Early warning for rug-pull risk, worsening slippage, or a market-maker leaving the venue. Useful as an exit or sizing guardrail.',
    evidence: 'Evidence centers on TVL snapshots, drop percentages over 6h/24h, and the token’s pool history. The graph context is the token and its liquidity venue.',
  },
  {
    id: 'EXCHANGE_FLOW',
    family: 'standard',
    title: 'Exchange Flow',
    tags: ['cex', 'deposit', 'withdrawal'],
    what: 'Detects net flows into or out of exchange wallets for a token.',
    why: 'Net exchange deposits often imply sell pressure; net withdrawals can imply accumulation, cold storage, or treasury movement.',
    evidence: 'Evidence is the direction of net flow, the token, and the magnitude of the imbalance over the lookback window.',
  },
  {
    id: 'MOMENTUM_DIVERGENCE',
    family: 'standard',
    title: 'Momentum Divergence',
    tags: ['price', 'fundamentals', 'divergence'],
    what: 'Flags when price moves one way while on-chain fundamentals move the other.',
    why: 'Classic quant warning sign: a move can be fading if real participation, volume, or TVL diverges from price.',
    evidence: 'Evidence compares price change, user counts, volume, and TVL trends across matched windows.',
  },
  {
    id: 'WASH_TRADE',
    family: 'standard',
    title: 'Wash Trade',
    tags: ['manipulation', 'volume', 'quality'],
    what: 'Identifies artificial volume patterns that look like round-tripping or recycled activity.',
    why: 'Helps you discount fake demand and avoid mistaking manufactured liquidity for real interest.',
    evidence: 'Evidence emphasizes sender/receiver repetition, round-trip ratios, and suspicious volume inflation metrics.',
  },
  {
    id: 'CONCENTRATION_SHIFT',
    family: 'standard',
    title: 'Concentration Shift',
    tags: ['holders', 'distribution', 'whale'],
    what: 'Tracks fast changes in holder concentration, especially top-wallet share.',
    why: 'A fast increase can mean a whale is building control; a fast decrease can mean distribution or insider exits.',
    evidence: 'Evidence is concentration deltas, top-holder share changes, and the wallets driving the shift.',
  },
  {
    id: 'TOKEN_QUALITY_SCORE',
    family: 'standard',
    title: 'Token Quality Score',
    tags: ['ranking', 'discovery', 'screening'],
    what: 'A composite score for ranking new or newly surfaced tokens.',
    why: 'Use it as a first-pass filter so analysts spend time on the best names first.',
    evidence: 'Evidence is the score breakdown across liquidity, distribution, security, sell health, and stability.',
  },
  {
    id: 'SMART_MONEY',
    family: 'standard',
    title: 'Smart Money',
    tags: ['wallets', 'profitable', 'copy-trade'],
    what: 'Finds tokens being accumulated by historically profitable wallets.',
    why: 'The on-chain version of watching quality investor behavior before it reaches price.',
    evidence: 'Evidence includes buyer wallet list, average win rate, average historical return, and the token being accumulated.',
  },
  {
    id: 'STEALTH_ACCUMULATION',
    family: 'standard',
    title: 'Stealth Accumulation',
    tags: ['small-buys', 'coordination', 'hidden'],
    what: 'Detects many small buys that add up to a large coordinated position.',
    why: 'Useful for spotting whales splitting size across wallets to avoid drawing attention.',
    evidence: 'Evidence is the aggregate USD amount, count of active buyers, coordinated time buckets, and the individual buy size ceiling.',
  },
  {
    id: 'INSIDER_TIMING',
    family: 'standard',
    title: 'Insider Timing',
    tags: ['pre-event', 'event', 'watchlist'],
    what: 'Looks backward from a major event to see whether wallets accumulated beforehand.',
    why: 'Useful for building a watchlist of wallets that appear to know something before the crowd.',
    evidence: 'Evidence compares pre-event buying to the prior baseline window and lists the wallets that were active.',
  },
  {
    id: 'VOLUME_PROFILE_ANOMALY',
    family: 'standard',
    title: 'Volume Profile Anomaly',
    tags: ['hours', 'size', 'distribution'],
    what: 'Detects shifts in when and how a token trades, including odd hour-of-day patterns and repeated trade sizes.',
    why: 'Can surface new trader cohorts, OTC-like distribution, or a change in who is controlling the market.',
    evidence: 'Evidence includes the 24-hour histogram shift, KL divergence, size clustering, and dominant trade bucket.',
  },
  {
    id: 'SPREAD_WIDENING',
    family: 'standard',
    title: 'Spread Widening',
    tags: ['slippage', 'liquidity', 'market-making'],
    what: 'Flags when expected slippage worsens faster than trading activity would justify.',
    why: 'A direct market-depth warning that liquidity is thinning even if the venue still looks active.',
    evidence: 'Evidence includes current vs prior TVL, estimated slippage ratio, trade activity, and median trade size.',
  },
];

function normalizeQuery(s) {
  return String(s || '').trim().toLowerCase();
}

export default function StoryReferencePage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = normalizeQuery(q);
    if (!needle) return STORY_TYPES;
    return STORY_TYPES.filter((t) => {
      const hay = `${t.id} ${t.title} ${(t.tags || []).join(' ')} ${t.what} ${t.why} ${t.evidence}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [q]);

  const sections = useMemo(() => {
    const rabbit = filtered.filter((x) => x.family === 'rabbit');
    const standard = filtered.filter((x) => x.family !== 'rabbit');
    return { rabbit, standard };
  }, [filtered]);

  const renderCard = (s) => {
    const isRabbit = s.family === 'rabbit';
    return (
      <div key={s.id} className={classes.card}>
        <div className={classes.cardHeader}>
          <div>
            <div className={classes.cardTitle}>{s.title}</div>
            <div className={classes.cardTypeRow}>
              <Chip className={`${classes.chip} ${isRabbit ? classes.chipRabbit : ''}`} label={s.id} size="small" />
              {(s.tags || []).slice(0, 6).map((tag) => (
                <Chip
                  key={`${s.id}-${tag}`}
                  className={classes.chip}
                  label={tag}
                  size="small"
                  style={{ opacity: 0.85 }}
                />
              ))}
            </div>
          </div>
        </div>
        <div className={classes.body}>
          <div>
            <span className={classes.label}>What it is:</span> {s.what}
          </div>
          <div style={{ marginTop: 8 }}>
            <span className={classes.label}>Why it matters:</span> {s.why}
          </div>
          <div style={{ marginTop: 8 }}>
            <span className={classes.label}>Evidence & graph shape:</span> {s.evidence}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>E3D Story Reference</div>
            <div className={classes.subtitle}>
              A canonical reference for every story type emitted by the current pipeline. Use this to interpret story intent, evidence, and expected graph structure.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button className={classes.ghostButton} variant="outlined" onClick={() => navigate('/story-guide')}>
              Story Guide
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/stories')}>
              Back to Stories
            </Button>
          </div>
        </div>
      </div>

      <div className={classes.controlsRow}>
        <TextField
          className={classes.searchField}
          variant="outlined"
          size="small"
          label=""
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search story types, tags, or descriptions…"
        />
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>Rabbit Forensics Stories</div>
        <div className={classes.body}>
          {'Rabbit stories are structure-first, bounded, explainable graph findings published into the Stories feed. They are designed to reconstruct investigation graphs purely from metadata.'}
        </div>
        <Divider className={classes.divider} />
        {sections.rabbit.length ? sections.rabbit.map(renderCard) : <div className={classes.body}>No matches.</div>}
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>Market + Activity Stories</div>
        <div className={classes.body}>
          {'These are non-rabbit pipeline stories (price, activity, discovery, macro). They are optimized for fast scanning and drilldown into /links and token views.'}
        </div>
        <Divider className={classes.divider} />
        {sections.standard.length ? sections.standard.map(renderCard) : <div className={classes.body}>No matches.</div>}
      </div>
    </div>
  );
}
